import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MenuIcon, XIcon, ArrowRightIcon } from 'lucide-react';
import { NAV_LINKS, GET_STARTED_URL, SIGN_IN_URL } from '../../data/dgpt';
import { CtaButton } from '../ui/CtaButton';
import { DgptLogo } from './DgptLogo';
import { trackEvent } from '../../utils/analytics';
import { cn } from '../../src/lib/utils';
export function DgptHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);
  useEffect(() => setOpen(false), [pathname]);
  const isActive = (href: string) => !href.includes('#') && href !== '/' && pathname.startsWith(href);
  const signInClass = 'text-sm font-medium text-text-secondary transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime focus-visible:ring-offset-4 focus-visible:ring-offset-ink';
  return <header className={cn('fixed inset-x-0 top-0 z-40 w-full border-b transition-colors duration-300', scrolled ? 'border-hairline bg-ink/90 backdrop-blur-xl' : 'border-transparent bg-ink/40 backdrop-blur-md')} data-id="element-36" data-mp="cmv84dk9g1bj">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-50 focus:rounded-md focus:bg-lime focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-ink" data-id="element-37" data-mp="riudhgfzjncz">
        Skip to main content
      </a>

      <div className="mx-auto flex h-16 w-full max-w-[1320px] items-center justify-between gap-6 px-4 sm:px-6 lg:h-[72px] lg:px-8" data-id="element-38" data-mp="22zaydpab6w78">
        <Link to="/" aria-label="DiligenceGPT™ home" className="shrink-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime focus-visible:ring-offset-4 focus-visible:ring-offset-ink" data-id="element-39" data-mp="3u2b5f3te3xlr">
          <DgptLogo data-id="element-40" data-mp="7s74uky0bg4z" />
        </Link>

        <nav aria-label="Primary" className="hidden lg:block" data-id="element-41" data-mp="3g9ljt0hrwup6">
          <ul className="flex items-center gap-8" data-id="element-42" data-mp="3g7qw5ef4aohe">
            {NAV_LINKS.map(link => {
            const active = isActive(link.href);
            return <li key={link.label} data-id="element-43" data-mp="3p38865jnivoc">
                  <Link to={link.href} aria-current={active ? 'page' : undefined} className={cn('relative text-sm font-medium transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime focus-visible:ring-offset-4 focus-visible:ring-offset-ink', active ? 'text-text-primary' : 'text-text-secondary')} data-id="element-44" data-mp="3p49k0c8hrm74">
                    {link.label}
                    {active ? <span aria-hidden="true" className="absolute -bottom-2 left-0 right-0 h-0.5 rounded-full bg-lime" data-id="element-45" data-mp="28jh5lgoa2l77" /> : null}
                  </Link>
                </li>;
          })}
          </ul>
        </nav>

        <div className="flex items-center gap-4" data-id="element-46" data-mp="317v4dyn6xljt">
          <a href={SIGN_IN_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('sign_in_click', {
          placement: 'header'
        })} className={cn(signInClass, 'hidden sm:inline')} data-id="element-47" data-mp="366py3vmp4uip">
            Sign In
          </a>
          <CtaButton href={GET_STARTED_URL} event="primary_cta_click" eventParams={{
          placement: 'header',
          cta: 'get_started'
        }} className="hidden sm:inline-flex" data-id="element-48" data-mp="1ft8w0d7t5948">
            Get Started
            <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-49" data-mp="211avlrnvjdg0" />
          </CtaButton>
          <button type="button" onClick={() => setOpen(value => !value)} aria-expanded={open} aria-controls="mobile-menu" className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-hairline text-text-secondary transition-colors hover:border-lime/50 hover:text-lime focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime lg:hidden" data-id="element-50" data-mp="1w7ow716q1625">
            {open ? <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-51" data-mp="2a04yu0vpn22j" /> : <MenuIcon className="h-5 w-5" aria-hidden="true" data-id="element-52" data-mp="29yto1h3uku1x" />}
            <span className="sr-only" data-id="element-53" data-mp="250jq4yqqnfcm">{open ? 'Close menu' : 'Open menu'}</span>
          </button>
        </div>
      </div>

      {open ? <div id="mobile-menu" className="border-t border-hairline bg-surface lg:hidden" data-id="element-54" data-mp="yjer2dbn226t">
          <nav aria-label="Mobile" className="mx-auto max-w-[1320px] px-4 py-3 sm:px-6" data-id="element-55" data-mp="tl3gu1hflpo2">
            <ul className="flex flex-col" data-id="element-56" data-mp="35bcoytmfpoc4">
              {NAV_LINKS.map(link => {
            const active = isActive(link.href);
            return <li key={link.label} data-id="element-57" data-mp="3f7o1nkpxsg73">
                    <Link to={link.href} onClick={() => setOpen(false)} aria-current={active ? 'page' : undefined} className={cn('flex min-h-[48px] items-center border-b border-hairline-soft text-[15px] font-medium transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-lime', active ? 'text-lime' : 'text-text-secondary')} data-id="element-58" data-mp="3f6ms5kokk1rh">
                      {link.label}
                    </Link>
                  </li>;
          })}
              <li data-id="element-59" data-mp="28jr7lajb9yzl">
                <a href={SIGN_IN_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('sign_in_click', {
              placement: 'mobile_menu'
            })} className="flex min-h-[48px] items-center border-b border-hairline-soft text-[15px] font-medium text-text-secondary hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-lime" data-id="element-60" data-mp="28lbzsti5l5ua">
                  Sign In
                </a>
              </li>
            </ul>
            <CtaButton href={GET_STARTED_URL} event="primary_cta_click" eventParams={{
          placement: 'mobile_menu',
          cta: 'get_started'
        }} size="lg" className="my-4 w-full" onClick={() => setOpen(false)} data-id="element-61" data-mp="2fftvmtqk2yi0">
              Get Started
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-62" data-mp="2y6jyiig4mlav" />
            </CtaButton>
          </nav>
        </div> : null}
    </header>;
}